import { AuthTokens, Contact } from '@uplift/types';
import { useCallback, useEffect, useState } from 'react';
import { SelectFactorModal } from './select.modal';
import { UpliftThemeProvider, useAlert } from '@uplift/ui-v2';
import { authSendOTP } from '@uplift/api';
import { VerifyModal } from './verify.modal';

export type MFAChallengeProps = {
  contacts: Contact[] | null;
  email: string;
  userId: string;
  onClose: () => void;
  onSuccess: (redirectUrl: string, tokens: AuthTokens) => void;
};

export const MFAChallenge: React.VFC<MFAChallengeProps> = ({ contacts, email, userId, onClose, onSuccess }) => {
  const alert = useAlert();
  const [ step, setStep ] = useState<'select' | 'verify'>('select');
  const [ contact, setContact ] = useState('');

  const submitId = useCallback(async (id: string) => {
    try {
      await authSendOTP(userId, {
        contactId: id !== 'email' ? id : undefined,
        useAccountEmail: id === 'email'
      });

      if (contact !== id) {
        setContact(id);
      }

      if (step === 'select') {
        setStep('verify');
      }
      alert.success('Requested new code');
    } catch (err) {
      if (err?.response?.data?.code) {
        alert.error(err.response.data.name);
      } else {
        alert.error('Error requesting code');
      }
    }
  }, [ alert, contact, step, userId ]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    if (step === 'select' && !contacts?.length) {
      setContact('email');
      setStep('verify');
      submitId('email');
    }
  }, [ contacts, step, submitId, userId ]);

  if (!userId) {
    return null;
  }

  switch (step) {
    case 'select':
      return (
        <UpliftThemeProvider>
          <SelectFactorModal
            contacts={contacts ?? []}
            email={email}
            open={Boolean(contacts)}
            onClose={onClose}
            onSubmit={submitId}
          />
        </UpliftThemeProvider>
      );
    case 'verify':
      return (
        <UpliftThemeProvider>
          <VerifyModal
            contact={contact}
            email={contact === 'email' ? email : undefined}
            phone={contact !== 'email' ? contacts.find(x => x.id === contact)?.value : undefined}
            userId={userId}
            onClose={onClose}
            onSendNewCode={() => submitId(contact)}
            onSuccess={onSuccess}
          />
        </UpliftThemeProvider>
      );
  }
};
